import { Link } from "react-router-dom";
import { FingerprintIcon as Fingerprint } from "@phosphor-icons/react";
import { fmtDate } from "@/lib/provenanceDisplay";

/**
 * Compact Evidence Summary (Iter 38) — mirrors the PDF section: one row per
 * IBM i command with batches, rows, last import and SHA-256 prefixes.
 */
export default function EvidenceSummaryCard({ workspaceId, evidence }) {
  const commands = evidence?.commands || [];
  if (commands.length === 0) return null;
  const hasLegacy = commands.some((c) => c.legacy_batches > 0);
  return (
    <div className="ml-card overflow-x-auto" data-testid="brief-evidence-section">
      <div className="px-4 pt-4 pb-2 flex items-start justify-between gap-3">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] flex items-center gap-1.5">
            <Fingerprint size={12} weight="bold" /> Evidence summary
          </div>
          <div className="text-sm text-[var(--ml-text-secondary)] mt-1">
            Which IBM i extracts back this workspace's evidence — command, batches, rows, last import, SHA-256 prefix.
          </div>
        </div>
        <Link to={`/workspaces/${workspaceId}/metadata/imports`} className="text-xs text-[var(--ml-primary)] hover:underline whitespace-nowrap" data-testid="brief-open-import-history">
          Import history →
        </Link>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-y border-[var(--ml-border)] bg-[var(--ml-surface)]">
            {["command", "feeds", "batches", "rows", "last import", "sha-256"].map((h) => (
              <th key={h} className="px-3 py-2 text-left ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {commands.map((c) => (
            <tr key={c.source_command} className="border-b border-[var(--ml-border)] last:border-b-0">
              <td className="px-3 py-2 ml-mono text-xs font-semibold">{c.source_command}</td>
              <td className="px-3 py-2 text-xs">{c.entities.join(", ")}</td>
              <td className="px-3 py-2 text-xs tabular-nums">
                {c.imported_batches}
                {c.legacy_batches > 0 && <span className="text-[#6929c4]"> +{c.legacy_batches} legacy</span>}
                {c.duplicate_attempts > 0 && <span className="text-[var(--ml-text-secondary)]"> ({c.duplicate_attempts} dup)</span>}
              </td>
              <td className="px-3 py-2 text-xs tabular-nums">{c.imported_rows.toLocaleString()}</td>
              <td className="px-3 py-2 ml-mono text-xs">{c.last_imported_at ? fmtDate(c.last_imported_at) : c.legacy_batches > 0 ? "legacy" : "—"}</td>
              <td className="px-3 py-2 ml-mono text-xs">{c.hash_prefixes.length ? c.hash_prefixes.join(", ") : c.legacy_batches > 0 ? "Unknown" : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {hasLegacy && (
        <p className="px-4 py-3 text-xs text-[#6929c4]" data-testid="brief-evidence-legacy-note">
          Legacy rows predate provenance tracking — their source command is inferred from the target table and is not proven.
        </p>
      )}
    </div>
  );
}
